import { useQuery } from "@tanstack/react-query";
import type { MappingPagedCollectionOfEventSearchResultAndEventGridItem } from "src/http/types";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import {
  exponentialBackoffRetryDelay,
  FIVE_RETRIES,
  STALE_TIME_ONE_HOUR,
} from "src/shared/utils/query/retry-constants";
import type { Dates } from "src/types/shared/cookies";

/**
 * Fetches the venue-page trending events. Hoisted out of VenueTrendingCarousel so the
 * "has recommendations" signal is known independently of the removal variant — the query
 * runs in both arms, keeping the participation gate pristine.
 */
export const useVenueTrendingEvents = (venueId: number, dates: Maybe<Dates>) => {
  const createQueryFn = useCreateQueryFn();
  const storePageEvent = useStorePageEvent();
  const { from, to } = dates?.dates ?? {};
  const fromValue = from ? (typeof from === "string" ? from : from.toJSON()) : undefined;
  const toValue = to ? (typeof to === "string" ? to : to.toJSON()) : undefined;

  return useQuery({
    queryKey: createQueryKey(QueryKeyPrefix.TRENDING_EVENTS, { venueId, fromValue, toValue }),
    queryFn: async (context) =>
      createQueryFn<MappingPagedCollectionOfEventSearchResultAndEventGridItem>({
        method: "TrendingEventsLocale",
        params: [
          ["categoryId", 0],
          ["maxRows", 20],
          ["page", 0],
          ["venueId", venueId],
          ...(fromValue ? [["fromDate", fromValue] as [string, string]] : []),
          ...(toValue ? [["toDate", toValue] as [string, string]] : []),
        ],
      })(context),
    enabled: Boolean(venueId),
    staleTime: STALE_TIME_ONE_HOUR,
    retry: FIVE_RETRIES,
    retryDelay: (attemptIndex) => {
      storePageEvent(PE_TYPE.LOGGING, PE_KEYS.VENUE_TRENDING_CAROUSEL_FETCH_RETRY_REQUEST, `ERROR-${attemptIndex + 1}`);
      return exponentialBackoffRetryDelay(attemptIndex);
    },
  });
};
